import { useState } from 'react';
import { useAppStore } from '../stores';
import { Schedule, isChildIn } from '../domain';

const getInitialFormData = (user: { uid: string } | null) => ({
  name: '',
  description: '',
  householdId: '',
  schedule: { type: 'minutes', interval: 1440 } as Schedule,
  labels: [] as string[],
  assignments: user ? [{ userId: user.uid, isResponsible: true }] : [],
});

export const useAddChoreForm = (isOpen: boolean, onSuccess: () => void) => {
  const { user } = useAppStore(state => state.auth);
  const { addChore, households: allHouseholds } = useAppStore(state => state.chores);
  // A child can't add chores, so not to a household they're a child in.
  const households = allHouseholds.filter(h => !isChildIn(h.household, user?.uid));

  const [formData, setFormData] = useState(getInitialFormData(user));

  const [loading, setLoading] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setFormData(getInitialFormData(user));
      setAddAnother(false);
    }
  }

  const householdId = formData.householdId || households[0]?.household.id || '';

  const members = households.find(h => h.household.id === householdId)?.members ?? [];

  const isValid = !!(formData.name && householdId);

  const toggleAssignment = (userId: string) => {
    setFormData(prev => {
      const exists = prev.assignments.find(a => a.userId === userId);
      return {
        ...prev,
        assignments: exists
          ? prev.assignments.filter(a => a.userId !== userId)
          : [...prev.assignments, { userId, isResponsible: false }],
      };
    });
  };

  const toggleResponsible = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      assignments: prev.assignments.map(a =>
        a.userId === userId ? { ...a, isResponsible: !a.isResponsible } : a
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isValid) return;

    setLoading(true);
    try {
      await addChore(formData.name, formData.description, formData.schedule, householdId, formData.assignments, formData.labels);
      if (addAnother) {
        setFormData(prev => ({ ...prev, name: '', description: '' }));
      } else {
        onSuccess();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData: { ...formData, householdId },
    setFormData,
    households,
    members,
    toggleAssignment,
    toggleResponsible,
    loading,
    isValid,
    handleSubmit,
    addAnother,
    setAddAnother,
  };
};
